<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Menubar from 'primevue/menubar'
import Avatar from 'primevue/avatar'
import type { MenuItem } from 'primevue/menuitem'
import { RouteName } from '@/router'
import { useAuth } from '@/composables/useAuth'
import { useAppConfig } from '@/composables/useAppConfig'
import { useLocale } from '@/composables/useLocale'
import ThemeToggle from '@/components/ui/ThemeToggle.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import AppIcon from '@/components/ui/AppIcon.vue'

const router = useRouter()
const { user, isAuthenticated, logout } = useAuth()
const { config } = useAppConfig()
const { t } = useLocale()

const currentYear = new Date().getFullYear()

const menuItems = computed<MenuItem[]>(() => [
  {
    label: t('common.navigation.dashboard'),
    icon: 'solar:home-2-bold',
    command: () => router.push({ name: RouteName.Dashboard }),
  },
  {
    label: t('common.navigation.pricing'),
    icon: 'solar:dollar-bold',
    command: () => router.push({ name: RouteName.BillingPricing }),
  },
])

const userMenuItems = computed<MenuItem[]>(() => {
  if (isAuthenticated.value) {
    return [
      {
        label: user.value?.email || t('common.navigation.user'),
        icon: 'solar:user-bold',
        items: [
          {
            label: t('common.navigation.profile'),
            icon: 'solar:user-bold',
            command: () => router.push({ name: RouteName.Profile }),
          },
          {
            label: t('common.navigation.settings'),
            icon: 'solar:settings-bold',
            command: () => router.push({ name: RouteName.Settings }),
          },
          {
            separator: true,
          },
          {
            label: t('common.navigation.logout'),
            icon: 'solar:logout-2-bold',
            command: async () => {
              await logout()
            },
          },
        ],
      },
    ]
  }

  return [
    {
      label: t('common.navigation.signIn'),
      icon: 'solar:login-2-bold',
      command: () => router.push({ name: RouteName.AuthLogin }),
    },
  ]
})

const getUserInitials = computed(() => {
  if (!user.value) return 'U'
  if (user.value.name) {
    const names = user.value.name.split(' ')
    return names.map((n) => n[0]).join('').toUpperCase().substring(0, 2)
  }
  return user.value.email[0]?.toUpperCase() || 'U'
})
</script>

<template>
  <div class="tw-min-h-screen tw-flex tw-flex-col tw-bg-surface-50">
    <!-- Header -->
    <header class="tw-sticky tw-top-0 tw-z-sticky tw-bg-surface-0 tw-shadow-sm">
      <div class="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">
        <div class="tw-flex tw-items-center tw-justify-between tw-h-16">
          <!-- Logo and main nav -->
          <div class="tw-flex tw-items-center tw-gap-8">
            <router-link
              :to="{ name: RouteName.Dashboard }"
              class="tw-flex tw-items-center tw-gap-2 tw-text-primary-600 dark:tw-text-primary-400 hover:tw-text-primary-700 dark:hover:tw-text-primary-300 tw-transition-colors"
            >
              <AppIcon :name="config.icon" size="xl" />
              <span class="tw-font-bold tw-text-lg tw-text-surface-900">{{ config.name }}</span>
            </router-link>
            <nav class="tw-flex tw-items-center tw-gap-1">
              <router-link
                v-for="(item, index) in menuItems"
                :key="index"
                :to="{ name: item.label === 'Dashboard' ? RouteName.Dashboard : RouteName.BillingPricing }"
                class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-text-surface-700 hover:tw-bg-surface-100 tw-transition-colors tw-text-sm tw-font-medium"
              >
                <AppIcon v-if="item.icon" :name="item.icon" />
                {{ item.label }}
              </router-link>
            </nav>
          </div>
          <!-- User menu -->
          <div class="tw-flex tw-items-center tw-gap-3">
            <!-- Theme Toggle -->
            <ThemeToggle />

            <Avatar
              v-if="isAuthenticated"
              :label="getUserInitials"
              shape="circle"
              class="tw-bg-primary-600 tw-text-white tw-font-medium tw-shadow-sm"
              aria-hidden="true"
              data-testid="user-avatar"
            />
            <BaseButton
              v-if="!isAuthenticated"
              :to="{ name: RouteName.AuthLogin }"
              variant="secondary"
              size="sm"
            >
              <AppIcon name="solar:login-2-bold" />
              {{ t('common.navigation.signIn') }}
            </BaseButton>
            <template v-else>
              <Menubar
                :model="userMenuItems"
                data-testid="user-menu"
                :pt="{
                  root: { class: 'tw-border-0 tw-bg-transparent tw-p-0' },
                  itemLink: { class: 'tw-rounded-lg tw-px-3 tw-py-2 tw-text-surface-700 hover:tw-bg-surface-100 tw-transition-colors' },
                }"
              />
            </template>
          </div>
        </div>
      </div>
      <div class="tw-border-b tw-border-surface-200"></div>
    </header>

    <!-- Main Content -->
    <main class="tw-flex-1">
      <div class="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-8">
        <slot />
      </div>
    </main>

    <!-- Footer -->
    <footer class="tw-bg-surface-0 tw-border-t tw-border-surface-200 tw-mt-auto">
      <div class="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-6">
        <div class="tw-text-center">
          <p class="tw-text-sm tw-text-surface-500">
            &copy; {{ currentYear }}
            <a
              :href="config.copyrightOwnerUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="tw-text-surface-500 hover:tw-text-surface-700 tw-transition-colors"
            >{{ config.copyrightOwner }}</a>. {{ t('common.footer.allRightsReserved') }}
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>
